import * as React from 'react'
import {
  Box,
  Button,
  Modal,
  ModalActions,
  ModalContent,
  ModalHeader,
  Text,
} from '@lightspeed/design-system-react'
import { ApiCustomer } from '@vend/types'
import { useT } from '@transifex/react'
import { StepBreadcrumbs } from '../StepBreadcrumbs'
import { STEPS } from '../types'
import {
  hasContactInfo,
  isInvalidEmail,
} from '../../../common/customer-contact-utils'
import { CustomerInfo, PartialCustomerGroup } from '../../../common/types'
import { SelectedCustomer } from '../../../common/SelectedCustomer'
import { CustomerSearch } from '../../../../../customer/customer-search/CustomerSearch'

interface Props {
  onClose: () => void
  onStepChange: (step: STEPS) => void
  onAdd: () => void
  onSelect: (customer: ApiCustomer | null) => void
  onContactChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  selectedCustomer: ApiCustomer | null
  newCustomer: CustomerInfo | null
  group?: PartialCustomerGroup
}

const shouldDisableNext = (customer: ApiCustomer | null) => {
  return (
    Boolean(!customer) || !hasContactInfo(customer) || isInvalidEmail(customer)
  )
}

export const Step1: React.FC<Props> = ({
  onClose,
  onStepChange,
  onAdd,
  onSelect,
  onContactChange,
  selectedCustomer,
  newCustomer,
  group,
}) => {
  const t = useT()

  const firstStepCopies = {
    title: t('Quote contact information', {
      _context: 'Title for customer step in quote modal.',
    }),
    customer: t('Customer', {
      _context: 'Input label for customer step in quote modal.',
    }),
    addExistingCustomer: t('Add an existing or a new customer to this quote', {
      _context: 'Description for customer step in quote modal.',
    }),
    doNotCreateQuote: t('Don’t create quote', {
      _context: 'Button text for customer step in quote modal.',
    }),
    customerPlaceholder: t('Select or add a customer', {
      _context: 'Input placeholder text for customer step in quote modal.',
    }),
    next: t('Next', {
      _context: 'Next button text for customer step in quote modal.',
    }),
  }

  return (
    <Modal onClose={onClose}>
      <StepBreadcrumbs step={STEPS.SELECT_CUSTOMER} />
      <ModalHeader>
        {firstStepCopies.title}
        <Text tag="p" className="vd-mt2">
          {firstStepCopies.addExistingCustomer}
        </Text>
      </ModalHeader>
      <ModalContent>
        <div className="vd-text-label vd-mb2 vd-mt2">
          {firstStepCopies.customer}
        </div>
        {selectedCustomer ? (
          <SelectedCustomer
            selectedCustomer={selectedCustomer}
            group={group}
            onContactChange={onContactChange}
            onSelect={onSelect}
          />
        ) : (
          <CustomerSearch
            placeholder={firstStepCopies.customerPlaceholder}
            onAdd={onAdd}
            onSelect={onSelect}
            searchValue={(newCustomer && newCustomer.firstName) || undefined}
          />
        )}
      </ModalContent>
      <ModalActions>
        <Box className="vd-btn-group">
          <Button
            variant="supplementary"
            onClick={() => onClose()}
            data-cy="button-back"
            data-track="not-create-quote"
          >
            {firstStepCopies.doNotCreateQuote}
          </Button>
          <Button
            variant="go"
            onClick={() => onStepChange(STEPS.REVIEW_DETAILS)}
            disabled={shouldDisableNext(selectedCustomer)}
            data-cy="button-next"
            data-track="next-review-details-step"
          >
            {firstStepCopies.next}
          </Button>
        </Box>
      </ModalActions>
    </Modal>
  )
}

Step1.displayName = 'Step1'
